import {
  createAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import SectionsApi from '../../middleware/api/Sections/SectionsApi'
import {
  incomingSections,
  incomingEmpDepartments,
  incomingSectionExist,
  SectionState,
  SectionData,
  EditData,
  incomingDepartments,
} from '../../types/Sections/SectionsTypes'
import { ValidationError } from '../../types/commonTypes'
import { AppDispatch, RootState } from '../../stateStore'

const initialState: SectionState = {
  getEmpDepartments: [],
  departments: [],
  sections: [],
  isLoading: false,
  error: null,
  selectedDepartment: null,
  isSectionExist: null,
  isLoadingNew: false,
}

export const getDepartmentsList = createAsyncThunk(
  'section/getDepartments',
  async (_, thunkApi) => {
    try {
      return await SectionsApi.getDepartments()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.message)
    }
  },
)

export const getEmpDepartment = createAsyncThunk(
  'section/getEmpDepartments',
  async (_, thunkApi) => {
    try {
      return await SectionsApi.getEmpDepartments()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.message)
    }
  },
)
export const setSelectedDepartment =
  createAction<incomingEmpDepartments | null>('section/setSelectedDepartment')

export const getSectionsList = createAsyncThunk<
  incomingSections[],
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('section/getSections', async (deptId, thunkApi) => {
  try {
    return await SectionsApi.getSections(deptId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

export const isSectionExist = createAsyncThunk<
  boolean,
  SectionData,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('section/isSectionExist', async (sectionData, thunkApi) => {
  const { departmentId, sectionName } = sectionData
  try {
    return await SectionsApi.isSectionExist(departmentId, sectionName)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
export const saveSection = createAsyncThunk<
  incomingSectionExist[],
  SectionData,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('section/saveSection', async (sectionData, thunkApi) => {
  const { departmentId, sectionName } = sectionData
  try {
    return await SectionsApi.saveSection(departmentId, sectionName)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
export const editSection = createAsyncThunk<
  incomingSectionExist[],
  EditData,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('section/editSection', async (sectionData, thunkApi) => {
  const { checklistsectionId, sectionName, departmentId } = sectionData
  try {
    return await SectionsApi.editSection(
      checklistsectionId,
      sectionName,
      departmentId,
    )
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

export const deleteSection = createAsyncThunk(
  'section/deleteSection',
  async (checklistsectionId: number, thunkApi) => {
    try {
      return await SectionsApi.deleteSection(checklistsectionId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)
const sectionSlice = createSlice({
  name: 'section',
  initialState,
  reducers: {
    setSelectedDepartment: (state, action) => {
      state.selectedDepartment = action.payload
    },
    updateSection: (state, action) => {
      const { sectionIndex, newSectionName } = action.payload
      state.sections[sectionIndex].sectionName = newSectionName
    },
    addSection: (state, action) => {
      state.sections.push({
        departmentId: action.payload.departmentId,
        sectionName: action.payload.sectionName,
        checklistsectionId: 0,
        departmentName: '',
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentsList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getDepartmentsList.fulfilled, (state, action) => {
        state.departments = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(getDepartmentsList.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(getSectionsList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getSectionsList.fulfilled, (state, action) => {
        state.sections = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(getSectionsList.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as ValidationError
      })
      .addCase(getEmpDepartment.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getEmpDepartment.fulfilled, (state, action) => {
        state.getEmpDepartments = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(getEmpDepartment.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addMatcher(
        isAnyOf(
          getDepartmentsList.fulfilled,
          getSectionsList.fulfilled,
          isSectionExist.fulfilled,
        ),
        (state) => {
          state.isLoading = false
        },
      )
      .addMatcher(
        isAnyOf(
          saveSection.fulfilled,
          editSection.fulfilled,
          isSectionExist.fulfilled,
        ),
        (state) => {
          state.isLoadingNew = false
        },
      )
      .addMatcher(
        isAnyOf(
          saveSection.pending,
          editSection.pending,
          isSectionExist.pending,
        ),
        (state) => {
          state.isLoadingNew = true
        },
      )
      .addMatcher(
        isAnyOf(
          saveSection.rejected,
          editSection.rejected,
          isSectionExist.rejected,
        ),
        (state) => {
          state.isLoadingNew = false
        },
      )
  },
})

export const { actions: sectionActions } = sectionSlice
export const sectionThunks = {
  getDepartmentsList,
  getEmpDepartment,
  getSectionsList,
  isSectionExist,
  saveSection,
  editSection,
  deleteSection,
}
const getDepartmentList = (state: RootState): incomingDepartments[] =>
  state.sectionList.departments
const getEmpDepartments = (state: RootState): incomingEmpDepartments[] =>
  state.sectionList.getEmpDepartments
const getSectionList = (state: RootState): incomingSections[] =>
  state.sectionList.sections
const selectedDepartment = (state: RootState): incomingEmpDepartments | null =>
  state.sectionList.selectedDepartment
const getIsLoadingNew = (state: RootState): boolean =>
  state.sectionList.isLoadingNew

export const sectionsSelectors = {
  getDepartmentList,
  getEmpDepartments,
  getSectionList,
  selectedDepartment,
  getIsLoadingNew,
}
export const sectionListServices = {
  ...sectionThunks,
  actions: sectionSlice.actions,
}
export default sectionSlice.reducer
